import * as numeral from 'numeral'
import * as moment from 'moment'
import { Action } from 'actions'
import { AccountType } from 'interfaces/accounts'
import { FullMoneyflowType } from 'interfaces/moneyflow'
import { MARK_DATA_STALE, PAGE_CHANGED, SET_FILTER_CATEGORY, SET_TIMEFRAME, RESET_FINANCIAL_STATE } from 'actions/globalActions'
import { CHANGE_TRANSACTION_CATEGORY, DELETE_MONEYFLOW_TRACKER } from 'actions/moneyflowActions'
import { doesChangeProperty } from 'helpers/propertyHelpers'
import { getAnchorPage } from 'helpers/pageHelpers'
import { RANDOMIZE_PEER_GROUP_RELATION, SELECT_RELATION, SET_RANDOM_RELATION } from 'actions/peergroupActions'
import { isFormSubmittedAction, isSuccessAction } from 'helpers/actionsHelpers'
import {
  FORM_CARS_EDIT,
  FORM_CREATE_INCOME_TRACKER,
  FORM_CREATE_SPENDING_TRACKER,
  FORM_EDIT_INCOME_TRANSACTION,
  FORM_EDIT_SPENDING_TRANSACTION,
  FORM_HOMES_EDIT,
  FORM_MANUAL_ACCOUNT,
  FORM_MEMBER_PROFILE,
  FORM_REDEMPTION,
  FORM_EDIT_TRANSACTIONS_TRANSACTION,
} from 'constants/forms'
import { INSIGHT_CTA_CLICK, RESET_INSIGHTS } from 'actions/insightsActions'
import { hasCTAFilters } from 'helpers/insightHelpers'
import { CREDIT_LINKED } from 'actions/creditActions'
import { EditTransactionFormSubmittedPayload } from 'interfaces/form'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { MoneyflowCategoryType } from 'interfaces/meta'
import { DATE_FORMAT_ISO_FULL, DATE_FORMAT_US } from 'constants/datetime'
import { isLiveUpdateWithStatus, isManualBalanceAccount, isProviderAccount, isProviderBalanceAccount } from 'helpers/linkageHelpers'
import { PERFORM_MIXED_ACCOUNT_OPERATION, POLL_PROVIDER_ACCOUNT, SET_MANUAL_BALANCE_ACCOUNT } from 'actions/linkageActions'
import { INCOME_URL, REWARDS_URL, SPENDING_URL, OPPORTUNITIES_URL } from 'tangle-shared'
import { ADD_ACCOMPLISHMENT } from 'actions/bountyActions'
import { isAccomplishedRewardTracking } from 'helpers/bountyHelpers'
import { isAccountOperationSucceed, isPollingProviderAccountReady } from 'helpers/accountHelpers'
import { InsightDependency } from 'interfaces/insights'
import * as flatten from 'lodash/flatten'
import * as values from 'lodash/values'
import { SELECT_FEED_FILTER, RESET_POSTS } from 'actions/feedActions'
import {
  RESET_ASSUMPTIONS,
  PATCH_ASSUMPTIONS,
  POST_FUTURE_LAYER,
  PATCH_FUTURE_LAYER,
  DELETE_FUTURE_LAYER,
  COMPLETE_FUTURE_LAYER,
} from 'components/features/future/actions'
import { CANCEL_SUBSCRIPTION } from 'actions/subscriptionActions'
import { CREATE_STRIPE_PAYMENT_METHOD, PATCH_STRIPE_PAYMENT_METHOD } from 'actions/stripeActions'
import { SetFilterByAccountsAction, ToggleComparisonAction } from 'components/features/pageFilters/actions'

export type DiscardTrigger = Action['type'] | ((a: Action) => boolean)

export const needDiscardData = (triggers: DiscardTrigger[]): ((a: Action) => boolean) =>
  (a) => triggers.some(
    (trigger) => typeof trigger === 'string'
      ? a.type === trigger
      : trigger(a)
  )

const transactionChangedDiscardTriggers: DiscardTrigger[] = [
  (a) => a.type === CHANGE_TRANSACTION_CATEGORY && isSuccessAction(a),
  isFormSubmittedAction(FORM_EDIT_SPENDING_TRANSACTION, FORM_EDIT_INCOME_TRANSACTION, FORM_EDIT_TRANSACTIONS_TRANSACTION),
]

/* --------------------------- MEMBER ------------------------------ */

export const MEMBER_INFO_REFRESH_TRIGGERS: DiscardTrigger[] = [
  (a) => a.type === MARK_DATA_STALE && a.payload === 'member',
  doesChangeProperty,
  (action) => {
    return !!(action.type === COMPLETE_FUTURE_LAYER && action.meta === 'success' && action.payload.params.set_income)
  },
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]

/* ------------------------- PEERGROUPS ------------------------ */

export const PEERGROUPS_DISCARD_TRIGGERS: DiscardTrigger[] = [
  CREDIT_LINKED,
  isFormSubmittedAction(FORM_MEMBER_PROFILE),
]

export const PEERGROUPS_TOTALS_DISCARD_TRIGGERS: DiscardTrigger[] = [
  ...PEERGROUPS_DISCARD_TRIGGERS,
  RANDOMIZE_PEER_GROUP_RELATION,
  RESET_FINANCIAL_STATE,
]

// use it for /peergroups/spending/trend/, /peergroups/assets/breakdown/ and such kind of endpoints
export const PEERGROUPS_DATA_DISCARD_TRIGGERS: DiscardTrigger[] = PEERGROUPS_TOTALS_DISCARD_TRIGGERS


/* ------------------------- MONEYFLOW --------------------------- */

const createEditTransactionDiscardTrigger = (predicate?: (pageType: FullMoneyflowType | null, prevType: MoneyflowCategoryType, nextType: MoneyflowCategoryType) => boolean): DiscardTrigger =>
  (a) => {

    let prevCategoryType: MoneyflowCategoryType = null
    let nextCategoryType: MoneyflowCategoryType = null
    let pageType: FullMoneyflowType | null = null
    let isActionToProcess = false
    if (a.type === CHANGE_TRANSACTION_CATEGORY && isSuccessAction(a)) {
      pageType = a.payload.params.type
      prevCategoryType = a.payload.params.transaction.category.type
      nextCategoryType = a.payload.params.category.type
      isActionToProcess = true
    }

    if (a.type === FORM_SUBMITTED_SUCCESSFULLY
      && isFormSubmittedAction(FORM_EDIT_SPENDING_TRANSACTION, FORM_EDIT_INCOME_TRANSACTION, FORM_EDIT_TRANSACTIONS_TRANSACTION)(a)
    ) {
      // TODO wacky type cast until we make forms strongly-typed
      const payload = a.payload as EditTransactionFormSubmittedPayload
      pageType = a.payload.formName === FORM_EDIT_SPENDING_TRANSACTION ? 'spending' : 'income'
      const { formValues } = payload
      const { transaction } = payload.extra

      const prevCategory = transaction.category
      const nextCategory = formValues.category

      prevCategoryType = prevCategory.type
      nextCategoryType = nextCategory.type

      isActionToProcess = prevCategory.id !== nextCategory.id
        || transaction.amount !== numeral(formValues.amount).value()
        || transaction.date !== moment(formValues.date, DATE_FORMAT_US).format(DATE_FORMAT_ISO_FULL)
    }

    return isActionToProcess
      ? (predicate ? predicate(pageType, prevCategoryType, nextCategoryType) : false)
      : false
  }

export const createMoneyflowTrendDiscardTriggers = (type: FullMoneyflowType | null): DiscardTrigger[] => [
  ...BALANCE_ACCOUNT_DISCARD_TRIGGERS,
  createEditTransactionDiscardTrigger(
    (_pageType, prevCategoryType, nextCategoryType) =>
      type !== null && (prevCategoryType === type || nextCategoryType === type),
  ),
  isLiveUpdateWithStatus('flushed'),
  RESET_FINANCIAL_STATE,
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]

export const createMoneyflowBreakdownDiscardTriggers = createMoneyflowTrendDiscardTriggers

export const createTransactionDiscardTriggers = (type: FullMoneyflowType): DiscardTrigger[] => {
  let discardOnPageChanged = false

  return [
    ...BALANCE_ACCOUNT_DISCARD_TRIGGERS,
    (a) => a.type === SET_TIMEFRAME && a.payload.type === type,
    (a) => a.type === SET_FILTER_CATEGORY && a.payload.type === type,
    (a) => discardOnPageChanged && a.type === PAGE_CHANGED && (a.payload.prev === INCOME_URL || a.payload.prev === SPENDING_URL),
    createEditTransactionDiscardTrigger(
      // discard state only if a page type is not the same as a trigger type
      // (i.e. we're on a spending page and the triggers operate an income transaction component)
      // and if either category (prev or next) corresponds to the trigger type (`type`)
      (pageType, prevCategoryType, nextCategoryType) => {
        if (type !== pageType) {
          return prevCategoryType === type || nextCategoryType === type
        }
        if (nextCategoryType && nextCategoryType !== pageType) {
          discardOnPageChanged = true
        }
        return false
      },
    ),
    (a) => Boolean(
      a.type === INSIGHT_CTA_CLICK
        && a.payload.cta
        && hasCTAFilters(a.payload.cta)
        && a.payload.options.isCausingInsightsReset
        && getAnchorPage(a.payload.cta.target) === type,
    ),
    isLiveUpdateWithStatus('flushed'),
    (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
  ]
}


/* ------------------------- BOUNTY --------------------------- */

export const REWARDS_DISCARD_TRIGGERS: DiscardTrigger[] = [
  (a) => a.type === PAGE_CHANGED && a.payload.next === REWARDS_URL,
  (a) => a.type === ADD_ACCOMPLISHMENT,
  (a) => a.type === POLL_PROVIDER_ACCOUNT && a.meta === 'success' && a.payload.response.providerAccountState.status === 'ready',
]

export const REDEMPTIONS_DISCARD_TRIGGERS: DiscardTrigger[] = [
  isFormSubmittedAction(FORM_REDEMPTION),
]

export const REWARD_BALANCE_DISCARD_TRIGGERS: DiscardTrigger[] = [
  (a) => a.type === ADD_ACCOMPLISHMENT && !isAccomplishedRewardTracking(a.payload),
]


/* ------------------------- ACCOUNT --------------------------- */

const PROVIDER_ACCOUNT_DISCARD_TRIGGERS: DiscardTrigger[] = [
  isPollingProviderAccountReady,
]

const BALANCE_ACCOUNT_DISCARD_TRIGGERS: DiscardTrigger[] = [
  ...PROVIDER_ACCOUNT_DISCARD_TRIGGERS,
  isAccountOperationSucceed,
]

const createManualAccountDiscardTrigger = (type: AccountType): DiscardTrigger =>
  (a) => {
    if (a.type === SET_MANUAL_BALANCE_ACCOUNT) {
      return a.payload.kind === type
    }

    if (
      a.type === PERFORM_MIXED_ACCOUNT_OPERATION && isSuccessAction(a) && isManualBalanceAccount(a.payload.params.account)
    ) {
      return a.payload.params.account.kind === type
    }

    return false
  }

const createBalanceAccountDiscardTrigger = (type: AccountType): DiscardTrigger =>
  (a) => {
    if (isPollingProviderAccountReady(a)) {
      return true
    }

    if (a.type === PERFORM_MIXED_ACCOUNT_OPERATION && isSuccessAction(a)) {
      const { account } = a.payload.params
      if (isProviderBalanceAccount(account)) {
        return account.kind === type
      }
      if (isProviderAccount(account)) {
        return true
      }
    }

    return false
  }

const createPropertyDiscardTrigger = (type: AccountType): DiscardTrigger =>
  (a) => type === 'assets' && doesChangeProperty(a)

export const createAccountsDiscardTriggers = (type: AccountType): DiscardTrigger[] => [
  ...transactionChangedDiscardTriggers,
  createManualAccountDiscardTrigger(type),
  createBalanceAccountDiscardTrigger(type),
  createPropertyDiscardTrigger(type),
  RESET_FINANCIAL_STATE,
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]

export const createAccountRatesDiscardTriggers = (type: AccountType): DiscardTrigger[] => [
  createBalanceAccountDiscardTrigger(type),
  createPropertyDiscardTrigger(type),
  (a) => {
    if (a.type === CHANGE_TRANSACTION_CATEGORY && isSuccessAction(a)) {
      const { transaction, category } = a.payload.params
      return transaction.category.id === 'fees' || category.id === 'fees'
    }

    if (a.type === FORM_SUBMITTED_SUCCESSFULLY && a.payload.formName === FORM_EDIT_SPENDING_TRANSACTION
      || a.type === FORM_SUBMITTED_SUCCESSFULLY && a.payload.formName === FORM_EDIT_INCOME_TRANSACTION
      || a.type === FORM_SUBMITTED_SUCCESSFULLY && a.payload.formName === FORM_EDIT_TRANSACTIONS_TRANSACTION) {
      const { extra, formValues } = a.payload

      return extra.transaction.category.id === 'fees' || formValues.category.id === 'fees'
    }

    return false
  },
  isLiveUpdateWithStatus('flushed'),
  RESET_FINANCIAL_STATE,
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]

export const createAccountBreakdownDiscardTriggers = (type: AccountType): DiscardTrigger[] => [
  createManualAccountDiscardTrigger(type),
  ...createAccountRatesDiscardTriggers(type),
  RESET_FINANCIAL_STATE,
]

export const createAccountTrendDiscardTriggers = (type: AccountType): DiscardTrigger[] => [
  (a) => type === 'assets' && doesChangeProperty(a),
  ...createAccountsDiscardTriggers(type),
  RESET_FINANCIAL_STATE,
]


/* ------------------------- PROVIDERS --------------------------- */
// TODO: test
export const PROVIDER_ACCOUNTS_DISCARD_TRIGGERS: DiscardTrigger[] = [
  isLiveUpdateWithStatus('buffered', 'flushed'),
]

// TODO: test
// manual accounts should be discarded the same way because they're requested from the same API
export const MANUAL_BALANCE_ACCOUNTS_DISCARD_TRIGGERS: DiscardTrigger[] = PROVIDER_ACCOUNTS_DISCARD_TRIGGERS

// TODO: test
export const PROVIDER_BALANCE_ACCOUNTS_DISCARD_TRIGGERS: DiscardTrigger[] = [
  isLiveUpdateWithStatus('flushed'),
]


/* ------------------------- NETWORTH --------------------------- */

export const NETWORTH_PAGE_DISCARD_TRIGGERS: DiscardTrigger[] = [
  doesChangeProperty,
  ...BALANCE_ACCOUNT_DISCARD_TRIGGERS,
  SET_MANUAL_BALANCE_ACCOUNT,
  isAccountOperationSucceed,
  isLiveUpdateWithStatus('flushed'),
  RESET_FINANCIAL_STATE,
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]


/* -------------------------- CREDIT ---------------------------- */

export const CREDIT_DISCARD_TRIGGERS: DiscardTrigger[] = [
  RESET_FINANCIAL_STATE,
  CREDIT_LINKED,
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]

export const CREDIT_TREND_DISCARD_TRIGGERS = CREDIT_DISCARD_TRIGGERS

/* ------------------------- INSIGHT --------------------------- */
// This list is about to change because right now back end may introduce new insights
// based on variables depending on other factors

export const INSIGHT_COMMON_REFRESH_TRIGGERS: DiscardTrigger[] = [
  RESET_INSIGHTS,
  ...PROVIDER_ACCOUNT_DISCARD_TRIGGERS,
  isLiveUpdateWithStatus('flushed'),
  isFormSubmittedAction(
    FORM_MANUAL_ACCOUNT,
    FORM_HOMES_EDIT,
    FORM_CARS_EDIT,
    FORM_MEMBER_PROFILE,
  ),
  (a) => a.type === INSIGHT_CTA_CLICK && a.payload.options.isCausingInsightsReset,
  RESET_FINANCIAL_STATE,
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
  (a) => a.type === ToggleComparisonAction.type && a.meta === 'success',
]

export const INSIGHT_REFRESH_TRIGGERS_BY_DEPENDENCY: Record<InsightDependency, DiscardTrigger[]> = {
  category: [SET_FILTER_CATEGORY],
  timeframe: [SET_TIMEFRAME],
  peergroupRelation: [SELECT_RELATION, SET_RANDOM_RELATION],
  spendingTracker: [
    isFormSubmittedAction(FORM_CREATE_SPENDING_TRACKER),
    (a) => a.type === DELETE_MONEYFLOW_TRACKER && isSuccessAction(a),
  ],
  incomeTracker: [
    isFormSubmittedAction(FORM_CREATE_INCOME_TRACKER),
    (a) => a.type === DELETE_MONEYFLOW_TRACKER && isSuccessAction(a),
  ],
  transaction: transactionChangedDiscardTriggers,
  pageChange: [
    (a) => a.type === PAGE_CHANGED && !!a.payload.prev,
  ],
  leftOpportunitiesPage: [
    (a) => a.type === PAGE_CHANGED && a.payload.prev === OPPORTUNITIES_URL,
  ],
  future: [
    (a) => a.type === CREATE_STRIPE_PAYMENT_METHOD && isSuccessAction(a),
    (a) => a.type === PATCH_STRIPE_PAYMENT_METHOD && isSuccessAction(a),
    (a) => a.type === CANCEL_SUBSCRIPTION && isSuccessAction(a),

    (a) => a.type === RESET_ASSUMPTIONS && isSuccessAction(a),
    (a) => a.type === PATCH_ASSUMPTIONS && isSuccessAction(a),
    (a) => a.type === POST_FUTURE_LAYER && isSuccessAction(a),
    (a) => a.type === PATCH_FUTURE_LAYER && isSuccessAction(a),
    (a) => a.type === DELETE_FUTURE_LAYER && isSuccessAction(a),
  ],
  dashboard: [
    (a) => a.type === CREATE_STRIPE_PAYMENT_METHOD && isSuccessAction(a),
    (a) => a.type === PATCH_STRIPE_PAYMENT_METHOD && isSuccessAction(a),
    (a) => a.type === CANCEL_SUBSCRIPTION && isSuccessAction(a),
  ],
  coaching: [],
  creditcard: [],
}

export const INSIGHT_DISCARD_TRIGGERS: DiscardTrigger[] = [
  ...INSIGHT_COMMON_REFRESH_TRIGGERS,
  ...flatten(values(INSIGHT_REFRESH_TRIGGERS_BY_DEPENDENCY)),
  (a) => a.type === ToggleComparisonAction.type && a.meta === 'success',
  (a) => a.type === SetFilterByAccountsAction.type && a.meta === 'success',
]

/* ------------------------- FEED --------------------------- */

export const POSTS_REFRESH_TRIGGER: DiscardTrigger[] = [
  SELECT_FEED_FILTER,
  RESET_POSTS,
]
